import { UseFormReturn, useWatch } from 'react-hook-form';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager, hasUnsupportedRemoteFormulaData } from '@utils/managers/FormulaManager';
import { handlerFieldWatch } from '@utils/fieldWatchUtils';
import { RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { EnsureExternalDataSourceLoaded, extractExternalDataSourceNames } from '@/utils/externalDataSourceUtils';
import {
    type FormulaTraceStatus,
    type IFormulaTraceTarget,
    DocumentFormulaTraceParentContext,
    useDocumentFormulaTraceRegistry,
} from '@/context/DocumentFormulaTraceContext';

export interface useFormulaStateOptions {
    /** Фоллбэк для пустого rules */
    fallback?: boolean;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
    trace?: IFormulaTraceTarget;
}
export interface IFormulaStateResult {
    /** Результат правила; до первого вычисления — `false`. Пустое rules отдаёт `fallback`
     *  сразу на первом рендере: вычислять нечего, и ждать эффекта незачем. */
    value: boolean;
    /**
     * Правило уже вычислено хотя бы раз (для пустого rules — `true` с первого рендера).
     * До этого `value` — не результат правила, а стартовое `false`, поэтому «переход»
     * false→true на первом вычислении не является сменой состояния
     * (см. useBecameVisible / useLateVisibilityCascadeGate).
     */
    settled: boolean;
}

export function useFormulaStateWithStatus<TFieldValues extends object>(
    rules: string | undefined,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<TFieldValues>,
    isEdit: boolean,
    isNew: boolean,
    activated: RefObject<boolean>,
    options?: useFormulaStateOptions,
): IFormulaStateResult {
    const fallback = options?.fallback ?? false;
    const runtime = useContext(DocumentRuntimeContext);
    const traceRegistry = useDocumentFormulaTraceRegistry();
    const traceParent = useContext(DocumentFormulaTraceParentContext);
    const requestedTraceTarget = options?.trace;
    const traceTargetId = requestedTraceTarget?.targetId;
    const traceTargetKind = requestedTraceTarget?.targetKind;
    const traceTargetName = requestedTraceTarget?.targetName;
    const traceRuleKind = requestedTraceTarget?.ruleKind;
    const remoteDataBlocked =
        runtime.mode === 'supportPreview' && hasUnsupportedRemoteFormulaData(rules, runtime.dataSource);
    const ensureExternalDataSourceLoaded = options?.ensureExternalDataSourceLoaded;
    const externalSourceNames = useMemo(() => (!isNew ? extractExternalDataSourceNames(rules) : []), [rules, isNew]);
    const [externalDataSourceReady, setExternalDataSourceReady] = useState(externalSourceNames.length === 0);

    const manager = useMemo(() => {
        if (!rules || remoteDataBlocked) return null;
        const m = new FormulaManager(rules, runtime.dataSource, runtime.requestCache);
        m.Init(fields, formMethods);
        return m;
    }, [rules, fields, formMethods, remoteDataBlocked, runtime.dataSource, runtime.requestCache]);

    // Селективный пересчёт при смене режима карточки (view↔edit): режимно-независимое правило
    // дало бы при перезапуске тот же результат, а на заявке с 1500 позициями это сотни лишних
    // прогонов (в т.ч. CHECKROW по всем строкам). Что именно делает правило режимно-зависимым,
    // знает разбор формулы — спрашиваем сам FormulaManager, а не сверяем текст правила регуляркой.
    // Актуальный режим для пересчётов по другим причинам эффекты читают из ref.
    const modeDependent = useMemo(() => manager?.DependsOnMode() ?? false, [manager]);
    const modeRef = useRef({ isEdit, isNew });
    modeRef.current = { isEdit, isNew };
    const modeKey = modeDependent ? `${isEdit}:${isNew}` : 'static';

    // `manager === null` ⟺ ветка «результат известен без вычисления» в эффекте ниже:
    // правила нет либо оно заблокировано в supportPreview. Тогда стартовое значение —
    // сразу `fallback`, а не `false` до пассивного эффекта: эффекты выполняются ПОСЛЕ
    // отрисовки кадра, и контрол, который до вычисления прячется (`visibility &&` в
    // рендере), успевал показать кадр без себя. Боксы разметки при этом на месте, поэтому
    // форма прыгала высотой на каждом показе — и на первой отрисовке карточки, и на
    // каждой смене строки в панели, где поддерево формы монтируется заново. Правило
    // видимости есть у ~13% контролов конфигурации, остальные схлопывались впустую.
    const [value, setValue] = useState<boolean>(() => (manager ? false : fallback));
    const [settled, setSettled] = useState<boolean>(() => !manager);
    const markSettled = useCallback(() => {
        if (activated.current) setSettled(true);
    }, [activated]);

    const publishTrace = useCallback(
        (result: unknown, status: FormulaTraceStatus, error?: string, watchedValues?: unknown[]) => {
            if (!traceRegistry || !traceTargetId || !traceTargetKind || !traceRuleKind || !rules) return;

            const dependencyFields = manager?.GetFields() ?? [];
            traceRegistry.upsert({
                targetId: traceTargetId,
                targetKind: traceTargetKind,
                targetName: traceTargetName,
                ruleKind: traceRuleKind,
                parentTargetId: requestedTraceTarget?.parentTargetId ?? traceParent?.targetId,
                parentTargetKind: requestedTraceTarget?.parentTargetKind ?? traceParent?.targetKind,
                source: rules,
                preparedExpression: manager?.GetPreparedExpression?.(),
                result,
                dependencies: dependencyFields.map((field, index) => ({
                    field,
                    value: watchedValues?.[index] ?? fields[field]?.value,
                })),
                externalSources: externalSourceNames,
                status,
                error,
            });
        },
        [
            externalSourceNames,
            fields,
            manager,
            rules,
            traceRegistry,
            traceRuleKind,
            traceTargetId,
            traceTargetKind,
            traceTargetName,
            requestedTraceTarget?.parentTargetId,
            requestedTraceTarget?.parentTargetKind,
            traceParent?.targetId,
            traceParent?.targetKind,
        ],
    );

    useEffect(() => {
        if (externalSourceNames.length === 0 || !ensureExternalDataSourceLoaded) {
            setExternalDataSourceReady(true);
            return;
        }

        setExternalDataSourceReady(false);
    }, [externalSourceNames, ensureExternalDataSourceLoaded]);

    useEffect(() => {
        if (externalSourceNames.length === 0 || !ensureExternalDataSourceLoaded) {
            return;
        }

        let canceled = false;
        void Promise.all(externalSourceNames.map((name) => ensureExternalDataSourceLoaded(name))).finally(() => {
            if (!canceled) {
                setExternalDataSourceReady(true);
            }
        });

        return () => {
            canceled = true;
        };
    }, [externalSourceNames, ensureExternalDataSourceLoaded]);

    useEffect(() => {
        if (!externalDataSourceReady) {
            return;
        }

        let canceled = false;
        if (rules && manager) {
            manager
                .EvalFormulaValues(modeRef.current.isEdit, modeRef.current.isNew)
                .then((result) => {
                    if (canceled) return;
                    publishTrace(result, 'evaluated');
                    if (activated.current) setValue(result);
                    markSettled();
                })
                .catch((error: unknown) => {
                    if (canceled) return;
                    publishTrace(undefined, 'error', error instanceof Error ? error.message : String(error));
                    console.error(error);
                    // Ошибка правила — тоже «вычислено»: значение остаётся стартовым и больше не изменится.
                    markSettled();
                });
        } else {
            setValue(fallback);
            markSettled();
            if (rules && remoteDataBlocked) {
                publishTrace(
                    fallback,
                    'fallback',
                    'Session-scoped источник для серверной функции формулы не настроен.',
                );
            } else if (!rules && traceRegistry && traceTargetId && traceTargetKind && traceRuleKind) {
                traceRegistry.remove({
                    targetId: traceTargetId,
                    targetKind: traceTargetKind,
                    targetName: traceTargetName,
                    ruleKind: traceRuleKind,
                });
            }
        }

        return () => {
            canceled = true;
        };
    }, [
        externalDataSourceReady,
        rules,
        manager,
        modeKey,
        fallback,
        activated,
        markSettled,
        publishTrace,
        remoteDataBlocked,
        traceRegistry,
        traceRuleKind,
        traceTargetId,
        traceTargetKind,
        traceTargetName,
    ]);

    // GetWatchFields отдаёт новый массив на каждый вызов, а useWatch по смене name переподписывается
    // в layout-эффекте (отписка со снимком значения, сверка, подписка) — на каждом рендере.
    const watchFields = useMemo(() => manager?.GetWatchFields() ?? [], [manager]);
    const watch = useWatch({
        name: watchFields,
    });

    useEffect(() => {
        if (!externalDataSourceReady) {
            return;
        }

        if (rules && manager) {
            void Promise.resolve(
                handlerFieldWatch(watch, modeRef.current.isEdit, modeRef.current.isNew, manager, setValue, activated, (result) => {
                    publishTrace(result, 'evaluated', undefined, watch);
                }),
            ).catch((error: unknown) => {
                publishTrace(undefined, 'error', error instanceof Error ? error.message : String(error), watch);
                console.error(error);
            });
        }
    }, [watch, modeKey, rules, manager, externalDataSourceReady, activated, publishTrace]);

    return { value, settled };
}

/** Значение правила без признака «вычислено» — для потребителей, которым важен только результат. */
export function useFormulaState<TFieldValues extends object>(
    rules: string | undefined,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<TFieldValues>,
    isEdit: boolean,
    isNew: boolean,
    activated: RefObject<boolean>,
    options?: useFormulaStateOptions,
): boolean {
    return useFormulaStateWithStatus(rules, fields, formMethods, isEdit, isNew, activated, options).value;
}
